import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../api/apiService';
import { PublicClientApplication } from '@azure/msal-browser';

interface File {
  doc_path: string;
  file_name: string;
  file_size: string;
  fileData?: string;
}

interface FileState {
  files: File[];
  currentFileIndex: number;
  loading: boolean;
  error: string | null;
}

const initialState: FileState = {
  files: [],
  currentFileIndex: 0,
  loading: false,
  error: null,
};

// Define the thunk API config type
export type ThunkApiConfig = {
  rejectValue: string;
  extra: {
    msalInstance: PublicClientApplication;
  };
};

export const fetchFiles = createAsyncThunk<
  File[],
  string,
  ThunkApiConfig
>(
  'files/fetchFiles',
  async (apiPath: string, { rejectWithValue, extra }) => {
    try {
      const response = await apiService(
        apiPath,
        "GET",
        undefined,
        undefined,
        extra.msalInstance
      );
      
      if (response.status === "success") {
        return response.data.files.map((file: any) => ({
          ...file,
          doc_path: `${file.doc_path}`,
        }));
      }
      return rejectWithValue(response.message || "Failed to fetch files.");
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : "An unknown error occurred");
    }
  }
);

export const fetchFileData = createAsyncThunk<
  { index: number; fileData: string },
  { docPath: string; index: number },
  ThunkApiConfig
>(
  'files/fetchFileData',
  async ({ docPath, index }, { rejectWithValue, extra }) => {
    try {
      const response = await apiService(
        `/download?doc_path=${encodeURIComponent(docPath)}`,
        "GET",
        undefined,
        undefined,
        extra.msalInstance
      );
      
      if (response.status === "success") {
        return { index, fileData: response.data };
      }
      return rejectWithValue(response.message || "Failed to fetch file data.");
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : "An unknown error occurred");
    }
  }
);

const fileSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    setCurrentFileIndex: (state, action: PayloadAction<number>) => {
      state.currentFileIndex = action.payload;
    },
    clearFiles: (state) => {
      state.files = [];
      state.currentFileIndex = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        state.loading = false;
        state.files = action.payload;
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch files";
      })
      .addCase(fetchFileData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFileData.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.index >= 0 && action.payload.index < state.files.length) {
          state.files[action.payload.index].fileData = action.payload.fileData;
        }
      })
      .addCase(fetchFileData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch file data";
      });
  },
});

export const { setCurrentFileIndex, clearFiles } = fileSlice.actions;
export default fileSlice.reducer;